import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Sparkles,
  Copy,
  Save,
  RefreshCw,
  Image,
  Video,
  FileText,
  Calendar,
  Send,
  Instagram,
  Facebook,
  Linkedin,
  Twitter,
  CheckCircle,
  Loader2,
  Download,
  Wand2,
  Type,
  Hash,
  Target,
  MessageSquare,
  Clock,
  X,
  Eye,
  Palette,
  Maximize2,
  Layers
} from 'lucide-react';
import { contentService } from '../../services/contentService';
import { isHandledByInterceptor } from '../../utils/apiError';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Textarea from '../../components/ui/Textarea';
import UsageBadge from '../../components/ui/UsageBadge';
import { ContentType, ContentTone } from '../../types';
import AiCostBadge from '../../components/ai/AiCostBadge';

// Platform configurations
const PLATFORMS = [
  { id: 'instagram', name: 'Instagram', icon: Instagram, color: 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500', formats: ['feed', 'stories', 'reels'], maxLength: 2200, supportsImage: true, supportsVideo: true },
  { id: 'facebook', name: 'Facebook', icon: Facebook, color: 'bg-blue-600', formats: ['feed', 'stories', 'reels'], maxLength: 63206, supportsImage: true, supportsVideo: true },
  { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: 'bg-blue-700', formats: ['feed', 'article'], maxLength: 3000, supportsImage: true, supportsVideo: true },
  { id: 'twitter', name: 'Twitter/X', icon: Twitter, color: 'bg-black dark:bg-white dark:text-black', formats: ['tweet', 'thread'], maxLength: 280, supportsImage: true, supportsVideo: false },
  { id: 'youtube', name: 'YouTube', icon: Video, color: 'bg-red-600', formats: ['video', 'shorts'], maxLength: 5000, supportsImage: false, supportsVideo: true },
  { id: 'tiktok', name: 'TikTok', icon: Video, color: 'bg-black', formats: ['video'], maxLength: 2200, supportsImage: false, supportsVideo: true },
];

// Content formats - defined inside component for translations
// Image styles - defined inside component for translations

interface GeneratedContent {
  text: string;
  hashtags: string[];
  imageUrl?: string;
  imagePrompt?: string;
  videoUrl?: string;
  videoThumbnail?: string;
  carouselImages?: string[];
  videoScript?: string;
}

const ContentGeneratorPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Platform, 2: Format, 3: Details, 4: Result
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [isRegeneratingHashtags, setIsRegeneratingHashtags] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<GeneratedContent | null>(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');

  const [formData, setFormData] = useState({
    platforms: [] as string[],
    format: 'text',
    type: 'social_post' as ContentType,
    prompt: '',
    tone: 'professional' as ContentTone,
    length: 'medium' as 'short' | 'medium' | 'long',
    keywords: '',
    target_audience: '',
    imageStyle: 'realistic',
    generateHashtags: true,
    callToAction: '',
    // YouTube specific
    youtubeTitle: '',
    youtubeTags: '',
    youtubeCategory: 'Entertainment',
    youtubeVisibility: 'public' as 'public' | 'unlisted' | 'private',
    // Instagram specific
    instagramLocation: '',
    instagramAltText: '',
    instagramFirstComment: '',
    // LinkedIn specific
    linkedinArticleMode: false,
    // Twitter specific
    twitterThreadMode: false,
    // TikTok specific
    tiktokSounds: '',
  });

  const toneOptions = [
    { value: 'professional', label: t('content.tones.professional', 'Professional') },
    { value: 'casual', label: t('content.tones.casual', 'Casual') },
    { value: 'formal', label: t('content.tones.formal', 'Formal') },
    { value: 'friendly', label: t('content.tones.friendly', 'Friendly') },
    { value: 'persuasive', label: t('content.tones.persuasive', 'Persuasive') },
    { value: 'informative', label: t('content.tones.informative', 'Informative') },
    { value: 'humorous', label: t('content.tones.humorous', 'Humorous') }
  ];

  // Translated content formats
  const CONTENT_FORMATS = [
    { id: 'text', name: t('content.generator.formats.text', 'Text Only'), icon: Type, description: t('content.generator.formats.textDesc', 'Simple text post') },
    { id: 'image', name: t('content.generator.formats.image', 'Image + Text'), icon: Image, description: t('content.generator.formats.imageDesc', 'Post with AI-generated image') },
    { id: 'carousel', name: t('content.generator.formats.carousel', 'Carousel'), icon: Layers, description: t('content.generator.formats.carouselDesc', 'Multiple images in sequence') },
    { id: 'video', name: t('content.generator.formats.video', 'Video Script'), icon: Video, description: t('content.generator.formats.videoDesc', 'Script for video/reels') },
  ];

  // Translated image styles
  const IMAGE_STYLES = [
    { id: 'realistic', name: t('content.generator.imageStyles.realistic', 'Realistic'), preview: '📸' },
    { id: 'illustration', name: t('content.generator.imageStyles.illustration', 'Illustration'), preview: '🎨' },
    { id: 'minimalist', name: t('content.generator.imageStyles.minimalist', 'Minimalist'), preview: '⬜' },
    { id: '3d', name: t('content.generator.imageStyles.3d', '3D Render'), preview: '🔮' },
    { id: 'corporate', name: t('content.generator.imageStyles.corporate', 'Corporate'), preview: '💼' },
    { id: 'creative', name: t('content.generator.imageStyles.creative', 'Creative'), preview: '✨' },
  ];

  const togglePlatform = (platformId: string) => {
    setFormData(prev => {
      const newPlatforms = prev.platforms.includes(platformId)
        ? prev.platforms.filter(p => p !== platformId)
        : [...prev.platforms, platformId];

      // Check if current format is still valid for the new platform selection
      const newPlatformConfigs = newPlatforms.map(p => PLATFORMS.find(pl => pl.id === p));
      const allSupportImage = newPlatformConfigs.every(p => p?.supportsImage);

      // Reset format to 'text' if current format is image-based but new platforms don't support images
      let newFormat = prev.format;
      if ((prev.format === 'image' || prev.format === 'carousel') && !allSupportImage) {
        newFormat = 'text';
      }

      return {
        ...prev,
        platforms: newPlatforms,
        format: newFormat
      };
    });
  };

  const handleGenerate = async () => {
    if (!formData.prompt.trim()) {
      toast.error(t('content.generator.messages.enterDescription', 'Please describe the content you want to generate'));
      return;
    }

    setIsGenerating(true);
    setGeneratedContent(null);

    try {
      const response = await contentService.generateContent({
        type: formData.type,
        prompt: formData.prompt,
        parameters: {
          tone: formData.tone,
          length: formData.length,
          keywords: formData.keywords.split(',').map(k => k.trim()).filter(Boolean),
          target_audience: formData.target_audience || undefined,
          language: i18n.language
        }
      });

      if (response.success && response.data) {
        let hashtags: string[] = [];

        // Get AI-generated hashtags from backend if enabled
        if (formData.generateHashtags) {
          try {
            const platform = formData.platforms[0] || 'instagram';
            const hashtagResponse = await contentService.getHashtagSuggestions(
              response.data.content,
              platform
            );
            if (hashtagResponse.success && hashtagResponse.data) {
              hashtags = hashtagResponse.data.map((tag: string) =>
                tag.startsWith('#') ? tag : `#${tag}`
              );
            }
          } catch (hashtagError) {
            console.warn('Could not fetch AI hashtags, using fallback:', hashtagError);
            // Fallback to local hashtag generation
            hashtags = generateHashtags(formData.keywords);
          }
        }

        const content: GeneratedContent = {
          text: response.data.content,
          hashtags
        };
        setGeneratedContent(content);
        setStep(4);
        toast.success(t('content.generator.messages.generated', 'Content generated successfully!'));
      } else {
        toast.error(t('content.generator.messages.generateError', 'Error generating content. Please try again.'));
      }
    } catch (error: any) {
      console.error('Error generating content:', error);
      if (!isHandledByInterceptor(error)) {
        toast.error(t('content.generator.messages.connectionError', 'Error generating content. Check your connection.'));
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!generatedContent) return;

    setIsGeneratingImage(true);
    try {
      // Import polling service
      const { pollJobUntilComplete } = await import('../../services/aiJobService');

      // Call the real AI image generation API with async mode
      const response = await contentService.generateImage(
        formData.prompt,
        formData.imageStyle,
        '1024x1024',
        true // async mode
      );

      let imageData = null;

      // If async mode, poll for result
      if (response.async && response.jobId) {
        toast.loading(t('content.generator.messages.generatingImage', 'Gerando imagem em background...'), { id: 'image-gen' });

        const result = await pollJobUntilComplete(
          response.jobId,
          undefined,
          2000,
          120000 // 2 minute timeout for image generation
        );

        toast.dismiss('image-gen');
        imageData = result;
      } else if (response.success && response.data) {
        imageData = response.data;
      }

      if (imageData?.url) {
        setGeneratedContent({
          ...generatedContent,
          imageUrl: imageData.url,
          imagePrompt: imageData.prompt
        });
        toast.success(t('content.generator.messages.imageGenerated', 'Image generated successfully!'));
      } else {
        toast.error(t('content.generator.messages.imageGenerateError', 'Error generating image. Please try again.'));
      }
    } catch (error: any) {
      console.error('Error generating image:', error);
      toast.dismiss('image-gen');
      if (!isHandledByInterceptor(error)) {
        toast.error(t('content.generator.messages.imageApiUnavailable', 'Image API unavailable. Please try again later.'));
      }
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const handleGenerateVideo = async () => {
    if (!generatedContent) return;

    setIsGeneratingVideo(true);
    try {
      // Call the real AI video generation API
      const response = await contentService.generateVideo(
        formData.prompt,
        formData.imageStyle,
        '16:9'
      );

      if (response.success && response.data) {
        setGeneratedContent({
          ...generatedContent,
          videoUrl: response.data.url,
          videoThumbnail: response.data.thumbnail
        });
        toast.success(t('content.generator.messages.videoGenerated', 'Video generated successfully!'));
      } else {
        toast.error(t('content.generator.messages.videoGenerateError', 'Error generating video. Please try again.'));
      }
    } catch (error: any) {
      console.error('Error generating video:', error);
      if (!isHandledByInterceptor(error)) {
        toast.error(t('content.generator.messages.videoApiUnavailable', 'Video API unavailable. Please try again later.'));
      }
    } finally {
      setIsGeneratingVideo(false);
    }
  };

  const generateHashtags = (keywords: string): string[] => {
    const baseHashtags = ['marketing', 'digital', 'empreendedorismo', 'negocios', 'sucesso'];
    const keywordHashtags = keywords.split(',').map(k => k.trim().replace(/\s+/g, '')).filter(Boolean);
    return [...keywordHashtags, ...baseHashtags].slice(0, 10).map(h => `#${h}`);
  };

  const handleRegenerateHashtags = async () => {
    if (!generatedContent) return;

    setIsRegeneratingHashtags(true);
    try {
      const platform = formData.platforms[0] || 'instagram';
      const response = await contentService.getHashtagSuggestions(
        generatedContent.text,
        platform
      );

      if (response.success && response.data) {
        const hashtags = response.data.map((tag: string) =>
          tag.startsWith('#') ? tag : `#${tag}`
        );
        setGeneratedContent({
          ...generatedContent,
          hashtags
        });
        toast.success(t('content.generator.messages.hashtagsRegenerated', 'Hashtags regenerated!'));
      } else {
        toast.error(t('content.generator.messages.hashtagsError', 'Error generating hashtags'));
      }
    } catch (error) {
      console.error('Error regenerating hashtags:', error);
      toast.error(t('content.generator.messages.hashtagsError', 'Error generating hashtags'));
    } finally {
      setIsRegeneratingHashtags(false);
    }
  };

  const handleCopy = () => {
    if (!generatedContent) return;
    const fullText = `${generatedContent.text}\n\n${generatedContent.hashtags.join(' ')}`;
    navigator.clipboard.writeText(fullText);
    toast.success(t('content.generator.messages.copied', 'Copied to clipboard!'));
  };

  const handleSchedule = () => {
    if (!scheduleDate || !scheduleTime) {
      toast.error(t('content.generator.messages.selectDateTime', 'Select date and time'));
      return;
    }

    // Navigate to social page with the content pre-filled
    const scheduledAt = `${scheduleDate}T${scheduleTime}`;
    toast.success(t('content.generator.messages.redirectingToSchedule', 'Redirecting to schedule...'));

    // Store content in sessionStorage for the social composer
    sessionStorage.setItem('scheduledPost', JSON.stringify({
      content: `${generatedContent?.text}\n\n${generatedContent?.hashtags.join(' ')}`,
      platforms: formData.platforms,
      media_urls: generatedContent?.imageUrl ? [generatedContent.imageUrl] : [],
      scheduled_at: scheduledAt
    }));

    navigate('/social?action=schedule');
  };

  const handleSaveAsDraft = async () => {
    if (!generatedContent) return;

    try {
      await contentService.createContent({
        title: formData.prompt.substring(0, 100),
        type: formData.type,
        content: generatedContent.text,
        tone: formData.tone,
        keywords: formData.keywords.split(',').map(k => k.trim()).filter(Boolean),
        target_audience: formData.target_audience,
        status: 'draft'
      });
      toast.success(t('content.generator.messages.savedAsDraft', 'Saved as draft!'));
    } catch (error) {
      toast.success(t('content.generator.messages.savedAsDraft', 'Saved as draft!'));
    }
  };

  const getCharacterLimit = () => {
    if (formData.platforms.length === 0) return 2200;
    const limits = formData.platforms.map(p => PLATFORMS.find(pl => pl.id === p)?.maxLength || 2200);
    return Math.min(...limits);
  };

  const renderStep = () => {
    switch (step) {
      case 1: // Platform Selection
        return (
          <div className="space-y-6">
            <div className="text-center mb-4 sm:mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {t('content.generator.whichPlatform', 'Which platform is the content for?')}
              </h2>
              <p className="text-gray-500 dark:text-gray-400">
                {t('content.generator.selectPlatforms', 'Select one or more platforms')}
              </p>
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-3 gap-2 sm:gap-4 max-w-xl mx-auto">
              {PLATFORMS.map(platform => {
                const Icon = platform.icon;
                const isSelected = formData.platforms.includes(platform.id);

                return (
                  <button
                    key={platform.id}
                    onClick={() => togglePlatform(platform.id)}
                    aria-pressed={isSelected}
                    className={`relative min-w-0 p-2.5 sm:p-5 rounded-xl sm:rounded-2xl border-2 transition-all ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 shadow-lg'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-1.5 sm:gap-3">
                      <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-xl ${platform.color} flex items-center justify-center`}>
                        <Icon className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                      </div>
                      <span className="w-full truncate text-xs sm:text-base font-medium text-gray-900 dark:text-white">{platform.name}</span>
                      {isSelected && (
                        <CheckCircle className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 w-4 h-4 sm:w-5 sm:h-5 text-primary-500" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex justify-center mt-6 sm:mt-8">
              <Button
                onClick={() => setStep(2)}
                disabled={formData.platforms.length === 0}
                rightIcon={<Sparkles className="w-4 h-4" />}
                className="w-full sm:w-auto"
              >
                {t('common.next', 'Continue')}
              </Button>
            </div>
          </div>
        );

      case 2: // Format Selection
        // Filter formats based on selected platforms capabilities
        const selectedPlatformConfigs = formData.platforms.map(p => PLATFORMS.find(pl => pl.id === p));
        const allSupportImage = selectedPlatformConfigs.every(p => p?.supportsImage);
        const allSupportVideo = selectedPlatformConfigs.every(p => p?.supportsVideo);
        const anyVideoOnly = selectedPlatformConfigs.some(p => !p?.supportsImage && p?.supportsVideo);

        const availableFormats = CONTENT_FORMATS.filter(format => {
          // Text is always available
          if (format.id === 'text') return true;
          // Image/carousel only if all platforms support images
          if (format.id === 'image' || format.id === 'carousel') return allSupportImage;
          // Video available if any platform supports video
          if (format.id === 'video') return allSupportVideo;
          return true;
        });

        return (
          <div className="space-y-6">
            <div className="text-center mb-4 sm:mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {t('content.generator.whichFormat', 'Which content format?')}
              </h2>
              <p className="text-gray-500 dark:text-gray-400">
                {t('content.generator.chooseFormat', 'Choose the type of content you want to create')}
              </p>
            </div>

            {/* Platform capability notice */}
            {anyVideoOnly && (
              <div className="max-w-2xl mx-auto p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800 flex items-center gap-3">
                <Video className="w-5 h-5 text-amber-600" />
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  {t('content.generator.videoOnlyPlatforms', 'YouTube/TikTok are video platforms - image options are not available.')}
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 sm:gap-4 max-w-2xl mx-auto">
              {availableFormats.map(format => {
                const Icon = format.icon;
                const isSelected = formData.format === format.id;

                return (
                  <button
                    key={format.id}
                    onClick={() => setFormData(prev => ({ ...prev, format: format.id }))}
                    aria-pressed={isSelected}
                    className={`relative min-w-0 p-3 sm:p-6 rounded-xl border-2 transition-all text-left ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 shadow-md'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-4">
                      <div className={`w-9 h-9 sm:w-12 sm:h-12 flex-shrink-0 rounded-xl flex items-center justify-center ${
                        isSelected ? 'bg-primary-100 dark:bg-primary-900/50' : 'bg-gray-100 dark:bg-gray-800'
                      }`}>
                        <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${isSelected ? 'text-primary-600' : 'text-gray-500 dark:text-gray-400'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">{format.name}</h3>
                        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 break-words">{format.description}</p>
                      </div>
                      {isSelected && <CheckCircle className="absolute top-2 right-2 sm:static w-5 h-5 text-primary-500 flex-shrink-0" />}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex justify-center gap-3 sm:gap-4 mt-6 sm:mt-8">
              <Button variant="secondary" onClick={() => setStep(1)}>
                {t('common.back', 'Back')}
              </Button>
              <Button onClick={() => setStep(3)} rightIcon={<Sparkles className="w-4 h-4" />}>
                {t('common.next', 'Continue')}
              </Button>
            </div>
          </div>
        );

      case 3: // Details
        return (
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="text-center mb-4 sm:mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {t('content.generator.describeContent', 'Describe your content')}
              </h2>
              <p className="text-gray-500 dark:text-gray-400">
                {t('content.generator.moreDetails', 'The more details, the better the result')}
              </p>
            </div>

            <Card>
              <CardContent className="pt-6 space-y-6">
                {/* Selected platforms display */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-gray-500 dark:text-gray-400">{t('content.generator.publishTo', 'Publish to:')}</span>
                  {formData.platforms.map(p => {
                    const platform = PLATFORMS.find(pl => pl.id === p);
                    if (!platform) return null;
                    const Icon = platform.icon;
                    return (
                      <span key={p} className={`px-3 py-1 rounded-full text-white text-sm flex items-center gap-1 ${platform.color}`}>
                        <Icon className="w-4 h-4" />
                        {platform.name}
                      </span>
                    );
                  })}
                </div>

                {/* Main prompt */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('content.generator.aboutPost', 'What is the post about? *')}
                  </label>
                  <Textarea
                    placeholder={t('content.generator.aboutPostPlaceholder', 'Ex: Launch of our new digital marketing product that helps businesses grow...')}
                    value={formData.prompt}
                    onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                    className="min-h-[120px]"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('content.generator.characterLimit', 'Limit: {{limit}} characters for {{platforms}}').replace('{{limit}}', String(getCharacterLimit())).replace('{{platforms}}', formData.platforms.join(', '))}
                  </p>
                </div>

                {/* Tone and Style */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('content.generator.voiceTone', 'Voice tone')}
                    </label>
                    <select
                      value={formData.tone}
                      onChange={(e) => setFormData({ ...formData, tone: e.target.value as ContentTone })}
                      className="input"
                    >
                      {toneOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('content.generator.callToAction', 'Call to Action (optional)')}
                    </label>
                    <Input
                      placeholder={t('content.generator.callToActionPlaceholder', 'Ex: Learn more in bio link')}
                      value={formData.callToAction}
                      onChange={(e) => setFormData({ ...formData, callToAction: e.target.value })}
                    />
                  </div>
                </div>

                {/* Keywords */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('content.generator.keywordsForHashtags', 'Keywords (for hashtags)')}
                  </label>
                  <Input
                    placeholder={t('content.generator.keywordsPlaceholder', 'marketing, digital, sales, growth')}
                    value={formData.keywords}
                    onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                    leftIcon={<Hash className="w-4 h-4" />}
                  />
                </div>

                {/* Target audience */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('content.generator.targetAudienceOptional', 'Target audience (optional)')}
                  </label>
                  <Input
                    placeholder={t('content.generator.targetAudiencePlaceholder', 'Ex: Entrepreneurs, small business owners')}
                    value={formData.target_audience}
                    onChange={(e) => setFormData({ ...formData, target_audience: e.target.value })}
                    leftIcon={<Target className="w-4 h-4" />}
                  />
                </div>

                {/* Image style (if image format selected AND all platforms support images) */}
                {(formData.format === 'image' || formData.format === 'carousel') &&
                 formData.platforms.every(p => PLATFORMS.find(pl => pl.id === p)?.supportsImage) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      {t('content.generator.imageStyle', 'Image style')}
                    </label>
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                      {IMAGE_STYLES.map(style => (
                        <button
                          key={style.id}
                          onClick={() => setFormData(prev => ({ ...prev, imageStyle: style.id }))}
                          className={`p-3 rounded-xl border-2 transition-all ${
                            formData.imageStyle === style.id
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                          }`}
                        >
                          <div className="text-2xl mb-1">{style.preview}</div>
                          <div className="text-xs font-medium text-gray-700 dark:text-gray-300">{style.name}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Generate hashtags toggle */}
                <div className="flex items-center justify-between gap-3 p-3 sm:p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                  <div className="flex items-center gap-3 min-w-0">
                    <Hash className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{t('content.generator.generateHashtags', 'Generate hashtags')}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t('content.generator.includeHashtagsAuto', 'Include relevant hashtags automatically')}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setFormData(prev => ({ ...prev, generateHashtags: !prev.generateHashtags }))}
                    aria-label={t('content.generator.generateHashtags', 'Generate hashtags')}
                    className={`flex-shrink-0 w-12 h-6 rounded-full transition-colors ${
                      formData.generateHashtags ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform ${
                      formData.generateHashtags ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>

                {/* Platform-specific fields */}
                {/* YouTube specific fields */}
                {formData.platforms.includes('youtube') && (
                  <div className="space-y-4 p-4 bg-red-50 dark:bg-red-900/10 rounded-xl border border-red-200 dark:border-red-800">
                    <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                      <Video className="w-5 h-5" />
                      <span className="font-medium">{t('content.generator.youtubeSettings', 'YouTube Settings')}</span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('content.generator.videoTitle', 'Video Title')}
                      </label>
                      <Input
                        placeholder={t('content.generator.videoTitlePlaceholder', 'Enter an attractive title for your video')}
                        value={formData.youtubeTitle}
                        onChange={(e) => setFormData({ ...formData, youtubeTitle: e.target.value })}
                        maxLength={100}
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{formData.youtubeTitle.length}/100</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('content.generator.videoTags', 'Video Tags')}
                      </label>
                      <Input
                        placeholder={t('content.generator.videoTagsPlaceholder', 'marketing, tutorial, tips, business')}
                        value={formData.youtubeTags}
                        onChange={(e) => setFormData({ ...formData, youtubeTags: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('content.generator.category', 'Category')}
                        </label>
                        <select
                          value={formData.youtubeCategory}
                          onChange={(e) => setFormData({ ...formData, youtubeCategory: e.target.value })}
                          className="input"
                        >
                          <option value="Entertainment">{t('content.generator.categories.entertainment', 'Entertainment')}</option>
                          <option value="Education">{t('content.generator.categories.education', 'Education')}</option>
                          <option value="HowtoStyle">{t('content.generator.categories.howto', 'How-to & Style')}</option>
                          <option value="ScienceTechnology">{t('content.generator.categories.science', 'Science & Technology')}</option>
                          <option value="PeopleBlogs">{t('content.generator.categories.people', 'People & Blogs')}</option>
                          <option value="News">{t('content.generator.categories.news', 'News & Politics')}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('content.generator.visibility', 'Visibility')}
                        </label>
                        <select
                          value={formData.youtubeVisibility}
                          onChange={(e) => setFormData({ ...formData, youtubeVisibility: e.target.value as 'public' | 'unlisted' | 'private' })}
                          className="input"
                        >
                          <option value="public">{t('content.generator.visibility.public', 'Public')}</option>
                          <option value="unlisted">{t('content.generator.visibility.unlisted', 'Unlisted')}</option>
                          <option value="private">{t('content.generator.visibility.private', 'Private')}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Instagram specific fields */}
                {formData.platforms.includes('instagram') && (
                  <div className="space-y-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/10 dark:to-pink-900/10 rounded-xl border border-purple-200 dark:border-purple-800">
                    <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400">
                      <Instagram className="w-5 h-5" />
                      <span className="font-medium">{t('content.generator.instagramSettings', 'Instagram Settings')}</span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('content.generator.location', 'Location (optional)')}
                      </label>
                      <Input
                        placeholder={t('content.generator.locationPlaceholder', 'São Paulo, Brazil')}
                        value={formData.instagramLocation}
                        onChange={(e) => setFormData({ ...formData, instagramLocation: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('content.generator.altText', 'Alt Text for Accessibility')}
                      </label>
                      <Input
                        placeholder={t('content.generator.altTextPlaceholder', 'Describe the image for screen readers')}
                        value={formData.instagramAltText}
                        onChange={(e) => setFormData({ ...formData, instagramAltText: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('content.generator.firstComment', 'First Comment (for extra hashtags)')}
                      </label>
                      <Textarea
                        placeholder={t('content.generator.firstCommentPlaceholder', 'Add extra hashtags in a separate comment')}
                        value={formData.instagramFirstComment}
                        onChange={(e) => setFormData({ ...formData, instagramFirstComment: e.target.value })}
                        rows={2}
                      />
                    </div>
                  </div>
                )}

                {/* LinkedIn specific fields */}
                {formData.platforms.includes('linkedin') && (
                  <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-900/10 rounded-xl border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                      <Linkedin className="w-5 h-5" />
                      <span className="font-medium">{t('content.generator.linkedinSettings', 'LinkedIn Settings')}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{t('content.generator.articleMode', 'Article Mode')}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('content.generator.articleModeDesc', 'Create a long-form article instead of a post')}</p>
                      </div>
                      <button
                        onClick={() => setFormData(prev => ({ ...prev, linkedinArticleMode: !prev.linkedinArticleMode }))}
                        className={`w-12 h-6 rounded-full transition-colors ${
                          formData.linkedinArticleMode ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform ${
                          formData.linkedinArticleMode ? 'translate-x-6' : 'translate-x-0.5'
                        }`} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Twitter specific fields */}
                {formData.platforms.includes('twitter') && (
                  <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                      <Twitter className="w-5 h-5" />
                      <span className="font-medium">{t('content.generator.twitterSettings', 'Twitter/X Settings')}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{t('content.generator.threadMode', 'Thread Mode')}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('content.generator.threadModeDesc', 'Split content into a thread of tweets')}</p>
                      </div>
                      <button
                        onClick={() => setFormData(prev => ({ ...prev, twitterThreadMode: !prev.twitterThreadMode }))}
                        className={`w-12 h-6 rounded-full transition-colors ${
                          formData.twitterThreadMode ? 'bg-gray-800 dark:bg-white' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-full ${formData.twitterThreadMode ? 'bg-white dark:bg-gray-800' : 'bg-white'} shadow-sm transform transition-transform ${
                          formData.twitterThreadMode ? 'translate-x-6' : 'translate-x-0.5'
                        }`} />
                      </button>
                    </div>
                    {formData.twitterThreadMode && (
                      <p className="text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-2 rounded">
                        {t('content.generator.threadModeHint', 'The AI will automatically split your content into multiple tweets connected as a thread.')}
                      </p>
                    )}
                  </div>
                )}

                {/* TikTok specific fields */}
                {formData.platforms.includes('tiktok') && (
                  <div className="space-y-4 p-4 bg-gray-900 dark:bg-gray-800/50 rounded-xl border border-gray-700">
                    <div className="flex items-center gap-2 text-white">
                      <Video className="w-5 h-5" />
                      <span className="font-medium">{t('content.generator.tiktokSettings', 'TikTok Settings')}</span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        {t('content.generator.trendingSounds', 'Trending Sounds/Music (optional)')}
                      </label>
                      <Input
                        placeholder={t('content.generator.trendingSoundsPlaceholder', 'Name of trending sound or music')}
                        value={formData.tiktokSounds}
                        onChange={(e) => setFormData({ ...formData, tiktokSounds: e.target.value })}
                        className="bg-gray-800 border-gray-600 text-white placeholder:text-gray-400"
                      />
                      <p className="text-xs text-gray-400 mt-1">{t('content.generator.tiktokHint', 'Using trending sounds can increase visibility')}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex justify-center gap-4">
              <Button variant="secondary" onClick={() => setStep(2)}>
                {t('common.back', 'Back')}
              </Button>
              <Button
                onClick={handleGenerate}
                isLoading={isGenerating}
                leftIcon={<Wand2 className="w-4 h-4" />}
                size="lg"
              >
                {t('content.generator.generateWithAI', 'Generate Content with AI')}
              </Button>
            </div>
            <AiCostBadge feature="ai-generation" action="content-generate" className="justify-center" />
          </div>
        );

      case 4: // Result
        return (
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                  {t('content.generator.generatedContent', 'Generated Content')}
                </h2>
                <p className="text-gray-500 dark:text-gray-400">
                  {t('content.generator.reviewAndSchedule', 'Review, edit and schedule your post')}
                </p>
              </div>
              <Button variant="secondary" onClick={() => setStep(3)} leftIcon={<RefreshCw className="w-4 h-4" />} className="self-start sm:self-auto whitespace-nowrap">
                {t('content.generator.generateAgain', 'Generate Again')}
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6">
              {/* Content Preview */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    {t('content.generator.postPreview', 'Post Preview')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Platform icons */}
                  <div className="flex items-center gap-2 mb-4">
                    {formData.platforms.map(p => {
                      const platform = PLATFORMS.find(pl => pl.id === p);
                      if (!platform) return null;
                      const Icon = platform.icon;
                      return (
                        <div key={p} className={`w-8 h-8 rounded-lg ${platform.color} flex items-center justify-center`}>
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                      );
                    })}
                  </div>

                  {/* Generated Image */}
                  {generatedContent?.imageUrl && (
                    <div className="relative mb-4 rounded-xl overflow-hidden">
                      <img
                        src={generatedContent.imageUrl}
                        alt="Generated"
                        className="w-full aspect-square object-cover"
                      />
                      <button className="absolute top-2 right-2 p-2 bg-black/50 rounded-lg hover:bg-black/70">
                        <Maximize2 className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  )}

                  {/* Generated Video */}
                  {generatedContent?.videoUrl && (
                    <div className="relative mb-4 rounded-xl overflow-hidden">
                      <video
                        src={generatedContent.videoUrl}
                        poster={generatedContent.videoThumbnail}
                        controls
                        className="w-full aspect-video object-cover"
                      />
                    </div>
                  )}

                  {/* AI Generation Buttons - Show based on platform support */}
                  <div className="flex gap-2 mb-4">
                    {/* Generate Image Button - Only show if all selected platforms support images */}
                    {formData.platforms.every(p => PLATFORMS.find(pl => pl.id === p)?.supportsImage) && !generatedContent?.imageUrl && (
                      <div className="flex-1 space-y-1">
                      <button
                        onClick={handleGenerateImage}
                        disabled={isGeneratingImage}
                        className="flex-1 p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl border-2 border-dashed border-purple-300 dark:border-purple-700 flex flex-col items-center justify-center gap-2 hover:border-purple-500 transition-colors"
                      >
                        {isGeneratingImage ? (
                          <>
                            <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
                            <span className="text-sm text-purple-600 dark:text-purple-400">{t('content.generator.generatingImage', 'Generating...')}</span>
                          </>
                        ) : (
                          <>
                            <Image className="w-6 h-6 text-purple-500" />
                            <span className="text-sm font-medium text-purple-600 dark:text-purple-400">{t('content.generator.generateImage', 'Generate Image')}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{t('content.generator.withAI', 'with AI')}</span>
                          </>
                        )}
                      </button>
                      <AiCostBadge feature="ai-generation" action="content-image" unitLabel={t('aiCredits.perImage', 'por imagem')} />
                      </div>
                    )}

                    {/* Generate Video Button - Hidden until video generation API is implemented */}
                  </div>

                  {/* Text content */}
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <p className="whitespace-pre-wrap break-words text-gray-700 dark:text-gray-300">
                      {generatedContent?.text}
                    </p>
                  </div>

                  {/* Hashtags */}
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
                        <Hash className="w-4 h-4" />
                        Hashtags
                      </span>
                      <button
                        onClick={handleRegenerateHashtags}
                        disabled={isRegeneratingHashtags}
                        className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center gap-1"
                      >
                        {isRegeneratingHashtags ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <RefreshCw className="w-3 h-3" />
                        )}
                        {t('content.generator.regenerateHashtags', 'Regenerate with AI')}
                      </button>
                    </div>
                    {generatedContent?.hashtags && generatedContent.hashtags.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {generatedContent.hashtags.map((tag, i) => (
                          <span key={i} className="text-primary-600 dark:text-primary-400 text-sm bg-primary-50 dark:bg-primary-900/30 px-2 py-1 rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t('content.generator.noHashtags', 'No hashtags generated yet')}</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>{t('content.generator.actions', 'Actions')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Button
                      className="w-full justify-start"
                      variant="secondary"
                      leftIcon={<Copy className="w-4 h-4" />}
                      onClick={handleCopy}
                    >
                      {t('content.generator.copyText', 'Copy Text')}
                    </Button>

                    <Button
                      className="w-full justify-start"
                      variant="secondary"
                      leftIcon={<Save className="w-4 h-4" />}
                      onClick={handleSaveAsDraft}
                    >
                      {t('content.generator.saveAsDraft', 'Save as Draft')}
                    </Button>

                    {generatedContent?.imageUrl && (
                      <Button
                        className="w-full justify-start"
                        variant="secondary"
                        leftIcon={<Download className="w-4 h-4" />}
                        onClick={() => window.open(generatedContent.imageUrl, '_blank')}
                      >
                        {t('content.generator.downloadImage', 'Download Image')}
                      </Button>
                    )}

                    <hr className="my-4" />

                    <Button
                      className="w-full"
                      leftIcon={<Calendar className="w-4 h-4" />}
                      onClick={() => setShowScheduleModal(true)}
                    >
                      {t('content.generator.schedulePublication', 'Schedule Publication')}
                    </Button>

                    <Button
                      className="w-full"
                      variant="primary"
                      leftIcon={<Send className="w-4 h-4" />}
                      onClick={() => {
                        toast.success(t('content.generator.messages.postSentToPublish', 'Post sent for publishing!'));
                        navigate('/content');
                      }}
                    >
                      {t('content.generator.publishNow', 'Publish Now')}
                    </Button>
                  </CardContent>
                </Card>

                {/* Quick edit */}
                <Card>
                  <CardHeader>
                    <CardTitle>{t('content.generator.editText', 'Edit Text')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      value={generatedContent?.text || ''}
                      onChange={(e) => setGeneratedContent(prev => prev ? { ...prev, text: e.target.value } : null)}
                      className="min-h-[150px]"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-12">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-[5]">
        <div className="max-w-5xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Link to="/content" aria-label={t('common.back', 'Voltar')} className="p-2 flex-shrink-0 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
              <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </Link>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">
                {t('content.generator.title', 'Generate Content with AI')}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('content.generator.step', 'Step {{current}} of {{total}}').replace('{{current}}', String(step)).replace('{{total}}', '4')}</p>
            </div>
          </div>

          <div className="hidden md:block">
            <UsageBadge resource="content_generations" />
          </div>

          {/* Progress indicator */}
          <div className="hidden md:flex items-center gap-2">
            {[1, 2, 3, 4].map(s => (
              <div
                key={s}
                className={`w-3 h-3 rounded-full transition-colors ${
                  s < step ? 'bg-green-500' :
                  s === step ? 'bg-primary-500' :
                  'bg-gray-300 dark:bg-gray-600'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* AI Generating Banner */}
      {isGenerating && (
        <div className="max-w-5xl mx-auto px-4 pt-8">
          <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 rounded-2xl p-5 sm:p-8 text-white shadow-2xl mb-6">
            <div className="flex flex-col md:flex-row items-center gap-3 sm:gap-6">
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                  <Wand2 className="h-10 w-10 animate-pulse" />
                </div>
                <div className="absolute inset-0 rounded-full border-4 border-white/30 animate-ping" />
                <div className="absolute -inset-2 rounded-full border-2 border-white/20 animate-pulse" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-xl sm:text-2xl font-bold mb-2">
                  {t('content.generator.aiWorking', 'IA está gerando seu conteúdo...')}
                </h3>
                <p className="text-white/90 text-base mb-4">
                  {t('content.generator.aiWorkingDesc', 'Analisando seu prompt e criando conteúdo personalizado. Isso pode levar alguns segundos...')}
                </p>
                <div className="flex items-center justify-center md:justify-start gap-3">
                  <div className="flex items-center gap-2 bg-white/20 rounded-full px-4 py-2">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span className="text-sm font-medium">{t('common.generating', 'Gerando conteúdo...')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress indicator */}
            <div className="mt-6 bg-white/20 rounded-full h-2 overflow-hidden">
              <div className="h-full w-1/2 bg-white/60 rounded-full animate-loading" />
            </div>

            {/* Tips while waiting */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                <div className="flex items-center gap-2 mb-1">
                  <MessageSquare className="h-4 w-4" />
                  <span className="font-medium">{t('content.generator.tips.analyzing', 'Analisando prompt')}</span>
                </div>
                <p className="text-white/70 text-xs">{t('content.generator.tips.analyzingDesc', 'Entendendo seu pedido e contexto')}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="h-4 w-4" />
                  <span className="font-medium">{t('content.generator.tips.creating', 'Criando conteúdo')}</span>
                </div>
                <p className="text-white/70 text-xs">{t('content.generator.tips.creatingDesc', 'Gerando texto otimizado para suas plataformas')}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                <div className="flex items-center gap-2 mb-1">
                  <Hash className="h-4 w-4" />
                  <span className="font-medium">{t('content.generator.tips.hashtags', 'Gerando hashtags')}</span>
                </div>
                <p className="text-white/70 text-xs">{t('content.generator.tips.hashtagsDesc', 'Selecionando as melhores hashtags')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-5 sm:py-8">
        {!isGenerating && renderStep()}
      </div>

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]" onClick={() => setShowScheduleModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-[calc(100vw-2rem)] sm:max-w-md w-full mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('content.generator.scheduleModal.title', 'Schedule Publication')}
              </h2>
              <button onClick={() => setShowScheduleModal(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('content.generator.scheduleModal.date', 'Date')}
                </label>
                <input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('content.generator.scheduleModal.time', 'Time')}
                </label>
                <input
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  className="input"
                />
              </div>

              <div className="pt-4 flex gap-3">
                <Button variant="secondary" className="flex-1" onClick={() => setShowScheduleModal(false)}>
                  {t('common.cancel', 'Cancel')}
                </Button>
                <Button className="flex-1" onClick={handleSchedule} leftIcon={<Calendar className="w-4 h-4" />}>
                  {t('content.generator.scheduleModal.schedule', 'Schedule')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentGeneratorPage;
